import { useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import type { PartInfo, EquipmentTreeRowFlat } from "../../models";

interface FormValues {
  serialNumber: string;
  make: string;
  model: string;
  vendor: string;
  warehouseNumber: string;
  drawingNumber: string;
  operatorNumber: string;
}

interface Props {
  open: boolean;
  selectedRow: EquipmentTreeRowFlat | null;
  onClose: () => void;
  onConfirm: (
    l2PartId: number,
    partInfo: PartInfo,
    operatorNumber: number,
  ) => void;
  /** Whether to show Make/Model/Vendor fields. Defaults to true (EAF). Set false for Caster. */
  showMakeModelVendor?: boolean;
  /** Whether to show Serial Number field. Defaults to true. Caster only shows for Copper Mold Tube. */
  showSerialNumber?: boolean;
  /** Whether to show Warehouse/Drawing Number fields. Defaults to false. Only Caster Mold uses these. */
  showWarehouseDrawing?: boolean;
}

export const EditPartModal = ({
  open,
  selectedRow,
  onClose,
  onConfirm,
  showMakeModelVendor = true,
  showSerialNumber = true,
  showWarehouseDrawing = false,
}: Props) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<FormValues>({
    mode: "onChange",
    defaultValues: {
      serialNumber: "",
      make: "",
      model: "",
      vendor: "",
      warehouseNumber: "",
      drawingNumber: "",
      operatorNumber: "",
    },
  });

  // Reset form with current part values when modal opens
  useEffect(() => {
    if (open && selectedRow?.PartInfo) {
      reset({
        serialNumber: selectedRow.PartInfo.SerialNumber ?? "",
        make: selectedRow.PartInfo.Make ?? "",
        model: selectedRow.PartInfo.Model ?? "",
        vendor: selectedRow.PartInfo.Vendor ?? "",
        warehouseNumber: selectedRow.PartInfo.WarehouseNumber ?? "",
        drawingNumber: selectedRow.PartInfo.DrawingNumber ?? "",
        operatorNumber: "",
      });
    } else if (open) {
      reset({
        serialNumber: "",
        make: "",
        model: "",
        vendor: "",
        warehouseNumber: "",
        drawingNumber: "",
        operatorNumber: "",
      });
    }
  }, [open, selectedRow, reset]);

  const handleConfirm = (data: FormValues) => {
    if (!selectedRow?.L2PartId) return;

    const partInfo: PartInfo = {
      Make: data.make,
      Model: data.model,
      Vendor: data.vendor,
      SerialNumber: data.serialNumber,
      WarehouseNumber: data.warehouseNumber,
      DrawingNumber: data.drawingNumber,
    };

    onConfirm(
      selectedRow.L2PartId,
      partInfo,
      parseInt(data.operatorNumber, 10),
    );
    onClose();
  };

  const getTitle = () => {
    if (selectedRow?.rowType === "sparePart") {
      return `Edit Part - ${selectedRow.L2PartName ?? "Spare Part"}`;
    }
    if (selectedRow?.rowType === "location") {
      return `Edit Part - ${selectedRow.PositionName ?? "Location"}`;
    }
    return "Edit Part";
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(handleConfirm)}>
        <DialogTitle>{getTitle()}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {showSerialNumber ? (
              <Grid item xs={6}>
                <Controller
                  name="serialNumber"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      size="small"
                      label="Serial Number"
                      error={!!errors.serialNumber}
                      helperText={errors.serialNumber?.message}
                    />
                  )}
                />
              </Grid>
            ) : null}
            {showMakeModelVendor ? (
              <>
                <Grid item xs={6}>
                  <Controller
                    name="make"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        size="small"
                        label="Make"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Controller
                    name="model"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        size="small"
                        label="Model"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Controller
                    name="vendor"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        size="small"
                        label="Vendor"
                      />
                    )}
                  />
                </Grid>
              </>
            ) : null}
            {showWarehouseDrawing ? (
              <>
                <Grid item xs={6}>
                  <Controller
                    name="warehouseNumber"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        size="small"
                        label="Warehouse Number"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Controller
                    name="drawingNumber"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        size="small"
                        label="Drawing Number"
                      />
                    )}
                  />
                </Grid>
              </>
            ) : null}
            <Grid item xs={12}>
              <Controller
                name="operatorNumber"
                control={control}
                rules={{
                  required: "Operator number is required",
                }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    label="Operator Number"
                    fullWidth
                    size="small"
                    error={!!error}
                    helperText={error?.message || "Enter your Operator Number"}
                    inputProps={{
                      inputMode: "numeric",
                    }}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, "");
                      field.onChange(value);
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={!isValid}
          >
            Save
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
